import React, { useMemo, useState } from 'react';
import { Box, Divider, Grid, Paper, Typography, Collapse, useMediaQuery, useTheme } from '@mui/material';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import type { PatientEncounter } from '../../services/api';
import PatientDetailListManager from './PatientDetailListManager';
import PatientDiagnosisManager from './PatientDiagnosisManager';
import PatientEncountersPanel from './PatientEncountersPanel';

type Detail = { category?: string; content?: string; source?: string; createdAt?: string; created_at?: string };

type Props = {
  spruceEntityId?: string;
  // snapshotDetails — used only to decide which panels have data (hide empty).
  details: Detail[];
  encounters: PatientEncounter[];
  loadingEncounters?: boolean;
  onOpenHistory?: () => void;
};

const overlineSx = {
  color: 'text.secondary',
  letterSpacing: '0.08em',
  fontWeight: 600,
  display: 'block',
} as const;

// A bordered panel mirroring Practice Fusion's Summary cards.
// Density pass: tightened padding/gap so more clinical rows fit per screen.
const Panel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Paper sx={{ p: { xs: 1, sm: 1.25 }, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
    {children}
  </Paper>
);

const PatientOverview: React.FC<Props> = ({ spruceEntityId, details, encounters, loadingEncounters, onOpenHistory }) => {
  const [showHist, setShowHist] = useState(false);
  const theme = useTheme();
  // Same breakpoint as the app chrome (see the breakpoint rule in MainLayout).
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Which categories actually have data (drives "hide empty panel").
  const present = useMemo(() => {
    const s = new Set<string>();
    (details || []).forEach((d) => {
      if (d.category && (d.content || '').trim()) s.add(d.category);
    });
    return s;
  }, [details]);
  const has = (c: string) => present.has(c);
  const histCount = useMemo(
    () => (details || []).filter((d) => d.category === 'prescriptions' && (d.content || '').trim()).length,
    [details],
  );

  // Latest value per vitals_* category (same latest-wins rule as
  // PatientClinicalStrip.latestDetails). Values are shown verbatim as stored.
  const latestVitals = useMemo(() => {
    const map: Record<string, Detail> = {};
    (details || []).forEach((d) => {
      const cat = d.category || '';
      if (!cat.startsWith('vitals_')) return;
      const createdAt = d.createdAt || d.created_at || '';
      if (!map[cat] || (createdAt && createdAt > (map[cat].createdAt || map[cat].created_at || ''))) {
        map[cat] = d;
      }
    });
    return map;
  }, [details]);
  const vital = (cat: string) => (latestVitals[cat]?.content || '').trim();

  // A category list panel (reuses the shared manager); hidden when empty.
  const CatPanel = (
    category: string,
    label: string,
    extra?: { secondaryLabel?: string; severityBadge?: boolean },
  ) =>
    has(category) ? (
      <Panel>
        <PatientDetailListManager
          spruceEntityId={spruceEntityId}
          category={category}
          label={label}
          secondaryLabel={extra?.secondaryLabel}
          severityBadge={extra?.severityBadge}
        />
      </Panel>
    ) : null;

  const colSx = { display: 'flex', flexDirection: 'column', gap: 1, minWidth: 0 } as const;

  // Panels defined once, placed per-breakpoint below. Priority order (per the
  // HX reorg): HX → Medications → Allergies → the rest → Diagnoses/ICD last.
  const hxPanel =
    has('past_medical_history') || has('ongoing_medical_conditions') ? (
      <Panel>
        {/* One merged list under a single header: the FIRST list's own header
            row carries the "HX" title + add button (no separate title row =
            no dead vertical space). Active/ongoing problems first, past
            events flow under the divider with their header suppressed. */}
        {has('ongoing_medical_conditions') ? (
          <PatientDetailListManager spruceEntityId={spruceEntityId} category="ongoing_medical_conditions" label="HX" />
        ) : (
          <PatientDetailListManager spruceEntityId={spruceEntityId} category="past_medical_history" label="HX" />
        )}
        {has('ongoing_medical_conditions') && has('past_medical_history') && (
          <>
            <Divider />
            <PatientDetailListManager spruceEntityId={spruceEntityId} category="past_medical_history" label="Major events" hideHeader />
          </>
        )}
      </Panel>
    ) : null;

  const medsPanel = (
    <Panel>
      <PatientDetailListManager
        spruceEntityId={spruceEntityId}
        category="current_medications"
        label="Medications"
        secondaryLabel="Directions"
      />
      {histCount > 0 && (
        <Box>
          <Typography
            component="button"
            type="button"
            onClick={() => setShowHist((v) => !v)}
            sx={{
              border: 0,
              bgcolor: 'transparent',
              cursor: 'pointer',
              color: 'primary.main',
              fontWeight: 600,
              fontSize: '0.8rem',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 0.5,
              px: 0,
            }}
          >
            {showHist ? <UnfoldLessIcon fontSize="small" /> : <UnfoldMoreIcon fontSize="small" />}
            {showHist ? 'Hide historical' : `Show historical (${histCount})`}
          </Typography>
          <Collapse in={showHist} unmountOnExit>
            <Box sx={{ mt: 1 }}>
              <PatientDetailListManager
                spruceEntityId={spruceEntityId}
                category="prescriptions"
                label="Historical / past prescriptions"
                secondaryLabel="Directions"
              />
            </Box>
          </Collapse>
        </Box>
      )}
    </Panel>
  );

  const allergiesPanel = CatPanel('allergies', 'Allergies', { severityBadge: true });

  // Vitals card — closes the gap where vitals were only reachable through the
  // pill strip / mobile chart sheet. Latest stored value per metric, shown as
  // a dense wrap of label·value pairs; hidden when nothing is documented.
  const vitalPairs = (
    [
      ['BP', vital('vitals_bp')],
      ['HR', vital('vitals_pulse')],
      ['Temp', vital('vitals_temp')],
      ['Resp', vital('vitals_resp')],
      ['SpO2', vital('vitals_spo2')],
      ['Wt', vital('vitals_weight')],
      ['Ht', vital('vitals_height')],
      ['Pain', vital('vitals_pain')],
      ['Smoking', vital('vitals_smoking')],
    ] as Array<[string, string]>
  ).filter(([, v]) => v);
  const vitalsPanel = vitalPairs.length ? (
    <Panel>
      <Typography variant="overline" sx={overlineSx}>Vitals</Typography>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', columnGap: 1.5, rowGap: 0.5, alignItems: 'baseline' }}>
        {vitalPairs.map(([label, value]) => (
          <Box key={label} sx={{ display: 'inline-flex', alignItems: 'baseline', gap: 0.5, minWidth: 0 }}>
            <Typography component="span" sx={{ fontSize: '0.64rem', color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
              {label}
            </Typography>
            <Typography component="span" sx={{ fontSize: '0.78rem', fontWeight: 600, wordBreak: 'break-word' }}>
              {value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Panel>
  ) : null;
  const familyPanel = CatPanel('family_history', 'Family History');
  const socialPanel = CatPanel('social_history', 'Social History');
  const immunizationsPanel = CatPanel('immunizations', 'Immunizations');

  const encountersPanel =
    loadingEncounters || encounters.length > 0 ? (
      <Panel>
        <PatientEncountersPanel
          encounters={encounters}
          loading={loadingEncounters}
          limit={5}
          onViewAll={onOpenHistory}
        />
      </Panel>
    ) : null;

  const diagnosesPanel = (
    <Panel>
      <Typography variant="overline" sx={overlineSx}>Diagnoses</Typography>
      <PatientDiagnosisManager spruceEntityId={spruceEntityId} />
    </Panel>
  );

  // Mobile: one flat stack in strict priority order (a stacked 3-column grid
  // would wedge Family/Social History between HX and Medications).
  if (isMobile) {
    return (
      <Box sx={{ ...colSx, mt: 0.5 }}>
        {hxPanel}
        {medsPanel}
        {allergiesPanel}
        {vitalsPanel}
        {familyPanel}
        {socialPanel}
        {immunizationsPanel}
        {encountersPanel}
        {diagnosesPanel}
      </Box>
    );
  }

  return (
    <Grid container spacing={1.25} sx={{ mt: 0.5 }} alignItems="flex-start">
      {/* Column A — HX & history (HX top-left = first in reading order) */}
      <Grid size={{ xs: 12, md: 4 }} sx={colSx}>
        {hxPanel}
        {familyPanel}
        {socialPanel}
      </Grid>

      {/* Column B — Medications & allergies (med-safety pair kept adjacent),
          then Vitals so the snapshot covers every chart-sheet section. */}
      <Grid size={{ xs: 12, md: 4 }} sx={colSx}>
        {medsPanel}
        {allergiesPanel}
        {vitalsPanel}
        {immunizationsPanel}
      </Grid>

      {/* Column C — Encounters, then Diagnoses/ICD (lowest visual priority:
          bottom-right) */}
      <Grid size={{ xs: 12, md: 4 }} sx={colSx}>
        {encountersPanel}
        {diagnosesPanel}
      </Grid>
    </Grid>
  );
};

export default PatientOverview;
